import React from "react";
import { Table as TanStackTable } from "@tanstack/react-table";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../../shadcn/ui/Button";

interface DataTablePaginationProps<T> {
  table: TanStackTable<T>;
  /**
   * Server-side pagination mode
   */
  serverPagination?: {
    pageIndex: number;
    pageSize: number;
    total: number;
    totalPages: number;
    onPageChange: (page: number) => void;
  };
}

export function DataTablePagination<T>({
  table,
  serverPagination,
}: DataTablePaginationProps<T>) {
  if (serverPagination) {
    // Server-side pagination
    const { pageIndex, total, totalPages, onPageChange } = serverPagination;
    const start = (pageIndex - 1) * serverPagination.pageSize + 1;
    const end = Math.min(pageIndex * serverPagination.pageSize, total);

    return (
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-4 border-t border-gray-200 bg-white">
        <div className="text-sm text-gray-600">
          Showing <span className="font-medium">{start}</span> to{" "}
          <span className="font-medium">{end}</span> of{" "}
          <span className="font-medium">{total}</span> results
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(pageIndex - 1)}
            disabled={pageIndex === 1}
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="hidden sm:inline ml-1">Previous</span>
          </Button>
          <div className="flex items-center gap-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (pageIndex <= 3) {
                pageNum = i + 1;
              } else if (pageIndex >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = pageIndex - 2 + i;
              }
              return (
                <Button
                  key={pageNum}
                  variant={pageIndex === pageNum ? "default" : "outline"}
                  size="sm"
                  onClick={() => onPageChange(pageNum)}
                  className="w-8 h-8 p-0"
                >
                  {pageNum}
                </Button>
              );
            })}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(pageIndex + 1)}
            disabled={pageIndex >= totalPages}
          >
            <span className="hidden sm:inline mr-1">Next</span>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  // Client-side pagination
  const {
    getState,
    getPageCount,
    previousPage,
    nextPage,
    setPageIndex,
    getCanPreviousPage,
    getCanNextPage,
  } = table;
  const { pagination } = getState();
  const pageCount = getPageCount();

  if (pageCount <= 1) return null;

  return (
    <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-white">
      <div className="text-sm text-gray-600">
        Page <span className="font-medium">{pagination.pageIndex + 1}</span> of{" "}
        <span className="font-medium">{pageCount}</span>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => previousPage()}
          disabled={!getCanPreviousPage()}
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="hidden sm:inline ml-1">Previous</span>
        </Button>
        <div className="flex items-center gap-1">
          {Array.from({ length: Math.min(5, pageCount) }, (_, i) => {
            let pageNum;
            if (pageCount <= 5) {
              pageNum = i;
            } else if (pagination.pageIndex <= 2) {
              pageNum = i;
            } else if (pagination.pageIndex >= pageCount - 3) {
              pageNum = pageCount - 5 + i;
            } else {
              pageNum = pagination.pageIndex - 2 + i;
            }
            return (
              <Button
                key={pageNum}
                variant={pagination.pageIndex === pageNum ? "default" : "outline"}
                size="sm"
                onClick={() => setPageIndex(pageNum)}
                className="w-8 h-8 p-0"
              >
                {pageNum + 1}
              </Button>
            );
          })}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => nextPage()}
          disabled={!getCanNextPage()}
        >
          <span className="hidden sm:inline mr-1">Next</span>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

